/**
 * ATTACH-01 chip for the inbox (Seth 2026-08-28: "Same thing for customers who
 * send in contact forms — attach email or phone if different"). Renders ONLY
 * when the conversation is matched to a customer AND the endpoint it's coming
 * from (text number / email address) is nowhere on that account. One tap →
 * contact row; the phone matcher (MATCH-02) then recognizes the number on the
 * next call, and the chip disappears.
 */
import { Check, Loader2, Plus } from "lucide-react";
import { useAccountEndpoints, useAttachEndpoint, last10, prettyPhone } from "./attachEndpoint";

export function AttachEndpointChip({
  custno,
  endpoint,
  name,
}: {
  custno: string;
  endpoint: string;
  /** The person's name when known (provider contact / account) — used as the contact's name. */
  name?: string | null;
}) {
  const eps = useAccountEndpoints(custno).data;
  const attach = useAttachEndpoint();
  const isEmail = endpoint.includes("@");
  const p10 = isEmail ? "" : last10(endpoint);
  if (!eps) return null;
  if (isEmail ? eps.emails.has(endpoint.trim().toLowerCase()) : p10.length !== 10 || eps.phones.has(p10)) return null;

  const pretty = isEmail ? endpoint.trim().toLowerCase() : prettyPhone(endpoint);
  return (
    <button
      type="button"
      disabled={attach.isPending}
      onClick={() =>
        attach.mutate({
          custno,
          name: name?.trim() || (isEmail ? "Email contact" : "Phone contact"),
          phone: isEmail ? null : endpoint,
          email: isEmail ? endpoint : null,
          source: "inbox",
          note: `Attached from the inbox — this ${isEmail ? "email" : "number"} wasn't on the account`,
        })
      }
      title={`Put ${pretty} on this customer's account so Pegasus recognizes it next time`}
      className="ml-2 inline-flex shrink-0 items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 transition-colors hover:bg-sky-100 disabled:opacity-60"
    >
      {attach.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : attach.isSuccess ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
      Attach {isEmail ? "this email" : "this number"} to the account
    </button>
  );
}
